import {convertAllErrorsFromBeToFormError, putAll, takeLatestF, Dictionary, validateSchema} from '@fl/cmsch-fe-library';
import {keys, isUndefined, omitBy} from 'lodash/fp';
import {SagaIterator} from 'redux-saga';
import {isEmpty, opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {ValidateBreederBindingManager} from 'api/gen/ValidateBreederBindingManager';
import {ValidateCreateBreederBindingAdmin} from 'api/gen/ValidateCreateBreederBindingAdmin';
import {t} from 'app/translations';
import {formHelpers} from 'utils/forms';
import {showBeError} from 'utils/show-be-error';

import {ValidateBreederErrorsSchema} from '../../types/errors/validate-breeder-errors';
import {
    subjectManagementFacilityFormName,
    SubjectManagementFacilityFormTag,
    SubjectManagementFacilityFormValuesErrors,
} from '../../types/subject-management-facility-form-values';
import {ValidateManagementBreederAction} from '../action';
import {simpleAdminSelector} from '../selector';
import {
    buildSubjectManagementCenterOptions,
    BuildSubjectManagementCenters,
} from './build-subject-management-center-options';

interface ApiTypes {
    approvedFrom: string;
    approvedTo: string | null;
    subjectId: number;
    userProfileId: number;
    farmIds: Array<number> | null;
    roleIds: Array<number> | null;
    userProfileRoleIds: Array<number>;
    tag: SubjectManagementFacilityFormTag;
}

const getValidateBreederApiValues = ({
    userProfileId,
    subjectId,
    farmIds,
    roleIds,
    approvedFrom,
    approvedTo,
    tag,
    userProfileRoleIds,
}: ApiTypes): ValidateBreederBindingManager => tag === 'ValidateCreateBreederBindingManager'
    ? ({
        tag,
        binding: {
            roleIds: opt(roleIds).orCrash('No roleIds found'),
            userProfileId,
            approvedFrom,
            approvedTo,
            farmIds: opt(farmIds).orCrash('No farmIds found'),
            breederId: subjectId,
        },
    })
    : ({
        tag,
        binding: {
            approvedTo,
            farmIds,
            // There is only one ID in the update
            userProfileBreederRoleId: opt(userProfileRoleIds).headIn().orCrash('no userProfileRoleIds found'),
        },
    });

const mapValidateBreederErrors = (
    errors: Dictionary<keyof ValidateCreateBreederBindingAdmin, string>,
): SubjectManagementFacilityFormValuesErrors => {
    const validErrors = validateSchema(ValidateBreederErrorsSchema, 'FE')(errors).binding;

    return omitBy(isUndefined)<SubjectManagementFacilityFormValuesErrors>({
        approvedFrom: validErrors.approvedFrom,
        approvedTo: validErrors.approvedTo,
        farmIds: validErrors.farmIds,
        roleIds: validErrors.roleIds,
        subjectId: validErrors.breederId,
        userProfileId: validErrors.userProfileId,
    });
};

// eslint-disable-next-line max-lines-per-function
function* execute(_: ValidateManagementBreederAction): SagaIterator {
    const {
        approvedFrom,
        approvedTo,
        farmIds,
        userProfileRoleIds,
        subjectId,
        roleIds,
        userProfileId,
        tag,
    } = (
        yield* select(formHelpers.formValues(subjectManagementFacilityFormName))
    ).orCrash(`no ${subjectManagementFacilityFormName} form values`);

    const farms = yield* select(simpleAdminSelector.managementBreedersFarms);

    const pristine = yield* select(formHelpers.isPristine(subjectManagementFacilityFormName));
    if (
        approvedFrom === null
        || subjectId === null
        || userProfileId === null
        || isEmpty(roleIds)
        || isEmpty(farmIds) && !isEmpty(farms)
        || pristine
        || (approvedTo !== null && approvedFrom > approvedTo)
    ) return; // synchronously not valid

    yield* put(formHelpers.startAsyncValidation(subjectManagementFacilityFormName));

    const buildCenters: BuildSubjectManagementCenters = {centerIds: farmIds, type: 'USERS_FARM'};

    const fIds = yield* call(buildSubjectManagementCenterOptions, buildCenters);

    const response = yield* call(
        Api.validateUserProfileBreederRoleBySubjectManager,
        getValidateBreederApiValues({
            subjectId,
            roleIds,
            userProfileId,
            approvedFrom,
            approvedTo,
            userProfileRoleIds: opt(userProfileRoleIds).orElse([]),
            farmIds: fIds,
            tag: opt(tag).orCrash('Tag does not exist'),
        }),
    );

    if (!response.isSuccess) {
        if (response.isBadRequest) {
            const errors = convertAllErrorsFromBeToFormError(response.data);
            const breederErrors = mapValidateBreederErrors(errors.errors);
            yield* put(formHelpers.stopAsyncValidation(subjectManagementFacilityFormName, breederErrors));
            const fieldsWithNewErrors = keys(breederErrors) as Array<keyof typeof breederErrors>;

            if (!isEmpty(fieldsWithNewErrors)) {
                yield* put(formHelpers.touch(subjectManagementFacilityFormName, ...fieldsWithNewErrors));
            }
        } else {
            yield putAll(showBeError(response, t('common')('validateEntry')));
        }
    } else {
        yield* put(formHelpers.stopAsyncValidation(subjectManagementFacilityFormName, {}));
    }
}

/** Runs the admin saga that validates breeder-management form values before save. */
export function* validateManagementBreederSaga(): SagaIterator {
    yield takeLatestF('admin/VALIDATE_MANAGEMENT_BREEDER', execute);
}
